import { bgPrimary } from '@osmosis/tailwind-ui/styles/background';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { Route } from '../../../hooks/useRouterPush';

import React, { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { usePathname, useSearchParams } from 'next/navigation';
import { getAuthPath } from '../../../lib/composer/url';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { useUserContext } from '@osmosis/nextjs-components/context/UserContext';
import { text5 } from '@osmosis/tailwind-ui/styles/text';
import { track } from '../../../lib/util/tracking';

type Props = {
  text: ReactNode;
  fullHeight?: boolean;
  expiredUser?: boolean;
  className?: string;
};

/**
 * BlurredBlockAuth component.
 */
const BlurredBlockAuth = ({
  text,
  fullHeight = false,
  expiredUser = false,
  className,
}: Props) => {
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const { getPlansUrl } = useUserContext();

  return (
    <Box
      className={cn(
        `absolute left-[-11px] right-[-11px] ${
          fullHeight ? 'top-0 h-full' : 'bottom-[-26px] h-1/2'
        } z-[10] flex flex-col items-center justify-center rounded-b-[10px] bg-[rgba(255,255,255,0.01)] backdrop-blur-[20px]`,
        className,
      )}
      data-testid="blurred-block"
    >
      <P
        className={` max-w-[434px] text-center text-base font-bold sm:text-lg ${text5} mb-4`}
      >
        {text}
      </P>
      <Flex className="flex-row items-center justify-center gap-8">
        {expiredUser ? (
          <a href={getPlansUrl()}>
            <Button
              size="m"
              cn={cn('px-8', bgPrimary)}
              rounded={true}
              data-testid="plans-button"
            >
              <FormattedMessage
                id="auth.plans"
                defaultMessage="Explore Plans"
              />
            </Button>
          </a>
        ) : (
          <>
            <a href={getAuthPath(Route.CREATE, pathName, searchParams)}>
              <Button
                size="m"
                data-testid="sign_up_button"
                className={`${bgPrimary} rounded-[10rem] px-8`}
                onClick={() =>
                  track('begin_registration', { source: 'video_lockout' }, [
                    'gtm',
                  ])
                }
              >
                <FormattedMessage
                  id="auth.sign_up"
                  defaultMessage="Create a free account"
                />
              </Button>
            </a>
            <a href={getAuthPath(Route.LOGIN, pathName, searchParams)}>
              <Button
                size="m"
                data-testid="sign_in_button"
                variant="outline"
                color="blue"
                className="rounded-[10rem] px-8"
              >
                <FormattedMessage id="common.signIn" defaultMessage="Sign in" />
              </Button>
            </a>
          </>
        )}
      </Flex>
    </Box>
  );
};

export default BlurredBlockAuth;
